import { ref, reactive, watch } from "vue";
import axios from "axios";
import { convertJsonToFormData } from '@/helpers'

export interface CustomField {
    id: string | number;
    label: string;
    type: string;
    options?: { label: string; value: string | number }[];
    validations: any;
    attribute_label?: string;
    attribute_type?: string;
}

interface UseCustomFieldsOptions {
    modelType: string;
    position: string | number;
    initialValues?: Record<string, any>;
}

export function useCustomFields(options: UseCustomFieldsOptions) {
    const fields = ref<any[]>([]);
    const values = reactive<Record<string, any>>({});
    const loading = ref(false);
    const error = ref<string | null>(null);

    /* -----------------------------
     Fetch Custom Fields
  ------------------------------ */
    const fetchFields = async () => {
        loading.value = true;
        error.value = null;
        try {
            const res = await axios.get(
                route("customfield.custom-fields.index"),
                {
                    params: {
                        customfieldable_type: options.modelType,
                        status: 1,
                    },
                }
            );

            fields.value = res.data.data || [];

            // Initialize empty values if not present
            fields.value.forEach((field) => {
                if (!(field.id in values)) {
                    values[field.id] = null;
                }
            });
        } catch (err: any) {
            error.value = err?.message || "Failed to load custom fields";
        } finally {
            loading.value = false;
        }
    };

    /* -----------------------------
     Set Initial Values (Edit Mode)
  ------------------------------ */
    const setValues = (newValues: Record<string, any>) => {
        Object.keys(values).forEach((key) => delete values[key]);
        Object.assign(values, newValues || {});
    };

    /* -----------------------------
     Reset Values
  ------------------------------ */
    const resetValues = () => {
        Object.keys(values).forEach((key) => {
            values[key] = null;
        });
    };

    // If initial values provided
    if (options.initialValues) {
        setValues(options.initialValues);
    }

    const generateValidations = () => {
        const rules: Record<string, any> = {};

        fields.value.forEach((field: CustomField) => {
            const fieldRules: Record<string, any> = {};

            const validations = field.validations;

            if (!validations) return;

            /* -------- REQUIRED -------- */
            if (validations.required) {
                fieldRules.required = {
                    required: true,
                    message: `${field.attribute_label} is required`,
                };
            }

            /* -------- RANGE -------- */
            if (validations.range) {
                if (validations.range.min !== undefined) {
                    fieldRules.min = {
                        min: validations.range.min,
                        message: `${field.attribute_label} must be at least ${validations.range.min}`,
                    };
                }

                if (validations.range.max !== undefined) {
                    fieldRules.max = {
                        max: validations.range.max,
                        message: `${field.attribute_label} must be at most ${validations.range.max}`,
                    };
                }
            }

            /* -------- REGEX -------- */
            if (validations.regex) {
                fieldRules.regex = {
                    pattern: new RegExp(validations.regex),
                    message: `${field.attribute_label} format is invalid`,
                };
            }

            /* -------- FILE SIZE -------- */
            if (validations.maxSize) {
                fieldRules.maxSize = {
                    maxSize: validations.maxSize,
                    message: `${field.attribute_label} must be under ${validations.maxSize} KB`,
                };
            }

            /* -------- UNIQUE (Backend) -------- */
            if (validations.unique) {
                fieldRules.unique = {
                    unique: true,
                    message: `${field.attribute_label} must be unique`,
                };
            }

            /* -------- FILE SIZE -------- */
            if (validations.maxSize) {
                fieldRules.maxSize = {
                    maxSize: validations.maxSize,
                    message: `${field.attribute_label} must be under ${validations.maxSize} KB`,
                };
            }

            /* -------- UNIQUE (Backend) -------- */
            if (validations.unique) {
                fieldRules.unique = {
                    unique: true,
                    message: `${field.attribute_label} must be unique`,
                };
            }

            /* -------- EMAIL -------- */
            if (field.attribute_type === 'email') {
                fieldRules.email = {
                    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: `${field.attribute_label} must be a valid email address`,
                };
            }

            /* -------- URL -------- */
            if (field.attribute_type === 'url') {
                fieldRules.url = {
                    pattern:
                        /^(https?:\/\/)?([\w\-])+\.{1}([a-zA-Z]{2,63})([\/\w\-.?=&%+]*)*\/?$/,
                    message: `${field.attribute_label} must be a valid URL`,
                };
            }

            rules[field.id] = fieldRules;
        });

        return rules;
    };

    return {
        fields,
        values,
        loading,
        error,
        fetchFields,
        setValues,
        resetValues,
        generateValidations,
    };
}

export const getFormPayload = async (
    customFieldValues: Record<string, any> = {},
    form: Record<string, any> = {}
): Promise<{ payload: any; config: any }> => {

    let hasFile = false

    for (const val of Object.values(customFieldValues)) {
        if (val instanceof File) {
            hasFile = true
            break
        }
    }

    if (!hasFile) {
        for (const val of Object.values(form)) {
            if (val instanceof File) {
                hasFile = true
                break
            }
        }
    }

    let payload: any
    let config: any = {}

    if (hasFile) {
        let formData = new FormData()
        formData = convertJsonToFormData(form);

        payload = formData

        if (form?.id) {
            formData.append("_method", "PUT");
        }

        config = {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        }

    } else {
        payload = {
            ...form,
            custom_fields: customFieldValues,
        }
    }

    return { payload, config }
}